/**
 * Contexte de disponibilité - Synchronisation globale
 * 
 * Permet à tous les composants d'écouter les changements de disponibilité
 * et de se rafraîchir automatiquement après un blocage/déblocage
 */

import { createContext, useContext, useCallback, useState } from "react";

interface AvailabilityContextType {
  // Notifier un changement de disponibilité
  notifyAvailabilityChange: (vehicleId: string) => void;
  // S'abonner aux changements pour un véhicule
  subscribe: (vehicleId: string, callback: () => void) => () => void;
}

const AvailabilityContext = createContext<AvailabilityContextType | undefined>(undefined);

export function AvailabilityProvider({ children }: { children: React.ReactNode }) {
  const [subscribers, setSubscribers] = useState<Map<string, Set<() => void>>>(new Map());

  const notifyAvailabilityChange = useCallback((vehicleId: string) => {
    const callbacks = subscribers.get(vehicleId);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.error("[AvailabilityContext] Erreur lors de la notification:", error);
        }
      });
    }
  }, [subscribers]);

  const subscribe = useCallback((vehicleId: string, callback: () => void) => {
    setSubscribers((prev) => {
      const newSubscribers = new Map(prev);
      if (!newSubscribers.has(vehicleId)) {
        newSubscribers.set(vehicleId, new Set());
      }
      newSubscribers.get(vehicleId)!.add(callback);
      return newSubscribers;
    });

    // Retourner une fonction de désabonnement
    return () => {
      setSubscribers((prev) => {
        const newSubscribers = new Map(prev);
        const callbacks = newSubscribers.get(vehicleId);
        if (callbacks) {
          callbacks.delete(callback);
          if (callbacks.size === 0) {
            newSubscribers.delete(vehicleId);
          }
        }
        return newSubscribers;
      });
    };
  }, []);

  return (
    <AvailabilityContext.Provider value={{ notifyAvailabilityChange, subscribe }}>
      {children}
    </AvailabilityContext.Provider>
  );
}

export function useAvailabilityContext() {
  const context = useContext(AvailabilityContext);
  if (!context) {
    throw new Error("useAvailabilityContext must be used within AvailabilityProvider");
  }
  return context;
}

